import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, SectionCard, ResponsiveTable, StatusBadge, EmptyState, CardSkeleton } from "@/admin/ui";
import { RefreshCw, Save } from "lucide-react";

export default function AdminCreemProducts() {
  const { t } = useLang();
  const [data, setData] = React.useState(null);
  const [creemProducts, setCreemProducts] = React.useState([]);
  const [drafts, setDrafts] = React.useState({});
  const [topupTest, setTopupTest] = React.useState("");
  const [topupLive, setTopupLive] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const load = () => api.get("/api/admin/creem/product-map").then((r) => {
    setData(r.data);
    const d = {}; for (const p of r.data.plans) { d[p.id] = { test: p.creem_product_id_test || "", live: p.creem_product_id_live || "" }; }
    setDrafts(d);
    const topup = r.data.topup || [];
    setTopupTest((topup.find((x) => x.mode === "test") || {}).creem_product_id || "");
    setTopupLive((topup.find((x) => x.mode === "live") || {}).creem_product_id || "");
  });
  React.useEffect(() => { load(); }, []);

  const fetchCreem = async () => {
    try {
      const { data } = await api.get("/api/admin/creem/products");
      setCreemProducts(data.items);
      toast.success(`Fetched ${data.items.length} products`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const savePlan = async (planId) => {
    setBusy(true);
    try {
      const d = drafts[planId];
      await api.put(`/api/admin/creem/product-map/plan/${planId}`, { creem_product_id_test: d.test, creem_product_id_live: d.live });
      toast.success(t("common.saved")); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };
  const saveTopup = async (mode) => {
    setBusy(true);
    try {
      await api.put("/api/admin/creem/product-map/topup", { mode, creem_product_id: mode === "test" ? topupTest : topupLive });
      toast.success(t("common.saved")); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-creem-products-page">
      <PageHeader title={t("admin.creem_products.title")} description={t("admin.creem_products.subtitle")}
        primary={<button onClick={fetchCreem} className="lp-btn-secondary text-xs py-1.5 px-3" data-testid="fetch-creem-products">
          <RefreshCw className="w-3.5 h-3.5" /> {t("admin.creem_products.list")}
        </button>} />

      {creemProducts.length > 0 && (
        <SectionCard title="Creem products">
          <div className="max-h-60 overflow-auto space-y-1 font-mono text-xs">
            {creemProducts.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-white/70 gap-2">
                <span className="truncate">{p.id}</span>
                <span className="text-white/50 shrink-0">{p.name} · ${(p.price / 100).toFixed(2)} {p.currency}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {!data ? <CardSkeleton h="h-40" /> : data.plans.length === 0 ? (
        <EmptyState title="No plans to map" />
      ) : (
        <ResponsiveTable minWidth={900}>
          <thead className="lp-table-sticky"><tr>
            <th>{t("admin.creem_products.plan_col.product")}</th>
            <th>{t("admin.creem_products.plan_col.tier")}</th>
            <th className="text-right">{t("admin.creem_products.plan_col.price")}</th>
            <th>{t("admin.creem_products.plan_col.test_id")}</th>
            <th>{t("admin.creem_products.plan_col.live_id")}</th>
            <th className="text-right"></th>
          </tr></thead>
          <tbody>
            {data.plans.map((p) => (
              <tr key={p.id} data-testid={`plan-map-${p.id}`}>
                <td className="text-white/80 text-xs">{p.product_name}</td>
                <td><StatusBadge tone="brand">{p.tier}</StatusBadge></td>
                <td className="text-right text-white/70 tabular-nums">${p.price_monthly}</td>
                <td>
                  <input className="lp-input py-1 text-xs font-mono max-w-[220px]" value={drafts[p.id]?.test || ""}
                    onChange={(e) => setDrafts({ ...drafts, [p.id]: { ...drafts[p.id], test: e.target.value } })} data-testid={`plan-map-${p.id}-test`} />
                </td>
                <td>
                  <input className="lp-input py-1 text-xs font-mono max-w-[220px]" value={drafts[p.id]?.live || ""}
                    onChange={(e) => setDrafts({ ...drafts, [p.id]: { ...drafts[p.id], live: e.target.value } })} data-testid={`plan-map-${p.id}-live`} />
                </td>
                <td className="text-right">
                  <button className="lp-btn-ghost" onClick={() => savePlan(p.id)} disabled={busy} data-testid={`plan-map-save-${p.id}`} aria-label="Save">
                    <Save className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}

      {data && (
        <SectionCard title={t("admin.creem_products.topup_title")}>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["test", topupTest, setTopupTest], ["live", topupLive, setTopupLive]].map(([mode, val, setter]) => (
              <div key={mode}>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{mode}</label>
                <div className="flex gap-2">
                  <input className="lp-input font-mono text-xs" value={val} onChange={(e) => setter(e.target.value)} data-testid={`topup-map-${mode}`} />
                  <button className="lp-btn-secondary text-xs py-1.5 px-3" onClick={() => saveTopup(mode)}>{t("common.save")}</button>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
